import { useCallback, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppState } from "../store/contextProvider";
import { AppActions } from "../store/reducers";
import { useDataFetch } from "./useDataFetch";
import { Config } from "../../config/config";
import { socketRef, closeSharedSocket } from "./sharedSocket";

function getWsUrl(apiBase) {
  if (!apiBase) return null;
  const url = apiBase.trim().replace(/^http/, "ws");
  return url.replace(/\/+$/, "");
}


export const useSocket = (staging = null) => {
  const navigate = useNavigate();
  const { config, updateStore } = useAppState();
  const { callAPI } = useDataFetch();
  const [feedData, setFeedData] = useState(null);
  const stagingRef = useRef(staging);

  useEffect(() => {
    stagingRef.current = staging;
  }, [staging]);

  const apiBase = config?.env?.API_BASE_URL || config?.env?.ORIGINAL_API_BASE_URL;

  const connect = useCallback(
    (profileID) => {
      if (!profileID) return;
      const ws = socketRef.current;
      // Reuse if socket exists and is OPEN or still CONNECTING (avoid closing and opening a second connection = duplicate error in same tab)
      const canReuse = ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING);
      if (canReuse) {
        // Same as webapp: reuse and re-subscribe so current component receives messages
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "error") {
              const errorMessage = data.data?.error || "Session already created for this profile. Duplicate connection prohibited.";
              navigate("/SystemError", { replace: true, state: { title: "Session Error", errorMessage } });
              return;
            }
            const currentStaging = stagingRef.current;
            if (!currentStaging || data.staging === currentStaging) {
              setFeedData(data);
              if (data.message != null && data.message !== "") {
                updateStore({ type: AppActions.AppendJobFeedLog, payload: data.message + "\n" });
              }
            }
          } catch (_) {}
        };
        return;
      }
      const wsUrl = getWsUrl(apiBase);
      if (!wsUrl) return;
      const base = wsUrl.replace(/\/+$/, "");
      const url = `${base}?profileID=${encodeURIComponent(profileID)}`;
      if (socketRef.current) {
        try {
          socketRef.current.close();
        } catch (_) {}
        socketRef.current = null;
      }
      try {
        const newWs = new WebSocket(url);
        socketRef.current = newWs;
        newWs.onopen = () => console.log("Partner API WebSocket connected");
        newWs.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "error") {
              const errorMessage = data.data?.error || "Session already created for this profile. Duplicate connection prohibited.";
              navigate("/SystemError", { replace: true, state: { title: "Session Error", errorMessage } });
              return;
            }
            const currentStaging = stagingRef.current;
            if (!currentStaging || data.staging === currentStaging) {
              setFeedData(data);
              if (data.message != null && data.message !== "") {
                updateStore({ type: AppActions.AppendJobFeedLog, payload: data.message + "\n" });
              }
            }
          } catch (_) {}
        };
        newWs.onclose = () => {
          socketRef.current = null;
        };
        newWs.onerror = (err) => {
          console.warn("Partner API WebSocket error", err);
        };
      } catch (err) {
        console.warn("Partner API WebSocket connect failed", err);
      }
    },
    [apiBase, config, navigate, updateStore]
  );

  const disconnect = useCallback(() => {
    closeSharedSocket();
  }, []);

  const startTrade = useCallback(
    async (payload) => {
      const res = await callAPI(Config.ITradeAPI.startTrade, payload);
      return res;
    },
    [callAPI]
  );

  const stopTrade = useCallback(async () => {
    const res = await callAPI(Config.ITradeAPI.stopTrade, {});
    return res;
  }, [callAPI]);

  return { connect, disconnect, feedData, startTrade, stopTrade };
};
